import { Injectable, signal, Signal, WritableSignal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../environments/environment'; // Adjust the import based on your project structure
import { AccountService } from './account.service';
import { User } from '@app/_models';
import { HttpClient } from '@angular/common/http';
import { AdminNotification } from '@app/_models/adminNotification';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection: signalR.HubConnection;
  public messages: string[] = [];
  public adminNotification: WritableSignal<AdminNotification> = signal(new AdminNotification());
  user: User | null = null;

  constructor(private accountService: AccountService, private http: HttpClient) {
    this.initializeConnection();
  }

  private initializeConnection(): void {
    this.user = this.accountService.userValue;
    if (!this.user) {
      console.error('User not logged in. Cannot establish SignalR connection.');
      return;
    }
    if (this.user.role !== 'admin') {
      console.error('User is not an admin. Cannot establish SignalR connection.', this.user.role);
      return;
    }

    // Call the backend to negotiate connection details
    this.http.post(`${environment.apiUrl}/signalr/negotiate`, { userId: this.user.id })
      .toPromise()
      .then(response => response as any)
      .then(data => {
        if (!data || !data.url || !data.accessToken) {
          throw new Error('Invalid negotiation response. Missing URL or access token.');
        }
        console.log('Negotiation response:', data);

        // Use the URL and token returned by the negotiation endpoint
        this.hubConnection = new signalR.HubConnectionBuilder()
          .withUrl(data.url, {
            accessTokenFactory: () => data.accessToken
          })
          .configureLogging(signalR.LogLevel.Information)
          .withAutomaticReconnect()
          .build();

        // Start the connection
        this.hubConnection
          .start()
          .then(() => console.log('SignalR connection started'))
          .catch(err => {
            console.error('Error starting SignalR connection:', err);
            this.handleConnectionError(err);
          });

        // Register event handlers
        this.registerOnServerEvents();
      })
      .catch(err => {
        console.error('Negotiation error:', err);
        this.handleConnectionError(err);
      });
  }

  private registerOnServerEvents(): void {
    this.hubConnection.on('ReceiveMessage', (message: AdminNotification) => {
      console.log('Received message:', message);
      message.receivedDateTime = new Date(); // Set the received date/time
      this.adminNotification.set(message);
      this.messages.push(message.notificationMessage);
    });
  }

  public sendMessage(message: string): void {
    this.hubConnection.invoke('SendMessage', message)
      .catch(err => console.error('Error sending message:', err));
  }

  private handleConnectionError(error: any): void {
    if (error.message.includes('Failed to fetch')) {
      console.error('Network error: Unable to reach the SignalR server. Please check your network connection or server URL.');
    } else {
      console.error('Unexpected error:', error);
    }
  }
}